import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PopoverArrowTypes } from 'constants/common';
import classNames from 'classnames';

interface PortalPopoverProps {
  anchorComponent: ({ isPanelOpen }: { isPanelOpen: boolean }) => React.ReactNode;
  children: React.ReactNode;
  show: boolean;
  onTogglePopover: () => void;
  onClosePopover: () => void;
  offset?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  width?: string;
  minWidth?: string;
  padding?: string;
  showArrow?: boolean;
  arrowType?: PopoverArrowTypes;
  arrowOffset?: string;
  arrowClassName?: string;
  highlightAnchor?: boolean;
  disabled?: boolean;
  containerClassName?: string;
  anchorComponentId?: string;
  portalRootId?: string; // Optional ID for a specific portal container
}

const PortalPopover: React.FC<PortalPopoverProps> = ({
  anchorComponent,
  children,
  show,
  onTogglePopover,
  onClosePopover,
  offset = {},
  width = '',
  minWidth = '',
  padding = 'p-2',
  showArrow = false,
  arrowType = PopoverArrowTypes.TOP,
  arrowOffset = '',
  arrowClassName = '',
  highlightAnchor = true,
  disabled = false,
  containerClassName = '',
  anchorComponentId = '',
  portalRootId = 'root'
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<Element | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [popoverRect, setPopoverRect] = useState<DOMRect | null>(null);
  const [mounted, setMounted] = useState(false);

  // Create or find the portal container
  useEffect(() => {
    setMounted(true);
    let container = document.getElementById(portalRootId);

    if (!container) {
      container = document.createElement('div');
      container.id = portalRootId;
      document.body.appendChild(container);
    }

    setPortalContainer(container);

    return () => {
      // Clean up only if we created the container and no other portals are using it
      if (container && container.childElementCount === 0) {
        document.body.removeChild(container);
      }
    };
  }, [portalRootId]);

  // Update anchor position for portal positioning
  useEffect(() => {
    if (!show || !anchorRef.current) return;

    const updateRects = () => {
      if (anchorRef.current) {
        setAnchorRect(anchorRef.current.getBoundingClientRect());
      }
      if (popoverRef.current) {
        setPopoverRect(popoverRef.current.getBoundingClientRect());
      }
    };

    updateRects();
    window.addEventListener('resize', updateRects);
    window.addEventListener('scroll', updateRects, true);

    return () => {
      window.removeEventListener('resize', updateRects);
      window.removeEventListener('scroll', updateRects, true);
    };
  }, [show]);

  // Close popover when clicking outside
  useEffect(() => {
    if (!show) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClosePopover();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, onClosePopover]);

  // Calculate popover position based on offset and arrow type
  const getPopoverPosition = () => {
    if (!anchorRect) return {};

    // Start with basic position
    const position: React.CSSProperties = {
      position: 'fixed',
      zIndex: 9999
    };

    // Adjust position based on arrow type and offset
    const offsetValues = offset;

    switch (arrowType) {
      case PopoverArrowTypes.TOP:
        position.left = anchorRect.left + anchorRect.width / 2;
        position.top = anchorRect.top;
        if (offsetValues.top) position.top += offsetValues.top;
        if (offsetValues.left) position.left += offsetValues.left;
        if (offsetValues.right) position.left -= offsetValues.right;
        break;
      case PopoverArrowTypes.BOTTOM:
        position.left = anchorRect.left + anchorRect.width / 2;
        position.bottom = window.innerHeight - anchorRect.bottom;
        if (offsetValues.bottom) position.bottom += offsetValues.bottom;
        if (offsetValues.left) position.left += offsetValues.left;
        if (offsetValues.right) position.left -= offsetValues.right;
        break;
      case PopoverArrowTypes.LEFT:
        position.top = anchorRect.top + anchorRect.height / 2;
        position.left = anchorRect.left;
        if (offsetValues.top) position.top += offsetValues.top;
        if (offsetValues.left) position.left += offsetValues.left;
        break;
      case PopoverArrowTypes.RIGHT:
        position.top = anchorRect.top + anchorRect.height / 2;
        position.right = window.innerWidth - anchorRect.right;
        if (offsetValues.top) position.top += offsetValues.top;
        if (offsetValues.right) position.right += offsetValues.right;
        break;
      default:
        position.top = anchorRect.bottom;
        position.left = anchorRect.left;
    }

    // Prevent popover from going outside the viewport
    if (popoverRect) {
      if ((position.left as number) + popoverRect.width > window.innerWidth) {
        position.left = window.innerWidth - popoverRect.width - 10;
      }

      if ((position.left as number) < 10) {
        position.left = 10;
      }

      if ((position.top as number) + popoverRect.height > window.innerHeight) {
        position.top = window.innerHeight - popoverRect.height - 10;
      }

      if ((position.top as number) < 10) {
        position.top = 10;
      }
    }

    return position;
  };

  // Render anchor component
  const renderAnchor = () => (
    <div
      ref={anchorRef}
      id={anchorComponentId || ''}
      className={`inline-block ${highlightAnchor && show ? 'rounded-md bg-highlightGray' : ''}`}
      onClick={!disabled ? onTogglePopover : undefined}>
      {anchorComponent({ isPanelOpen: show })}
    </div>
  );

  // Render popover content in portal
  const renderPortalContent = () => {
    if (!show || !mounted || !portalContainer) return null;

    const popoverStyle = getPopoverPosition();

    return createPortal(
      <div
        ref={popoverRef}
        className={`${width} ${minWidth} ${padding} shadow-popover rounded-md bg-white ${containerClassName} shadow-lg`}
        style={popoverStyle}>
        {showArrow && (
          <div
            className={classNames('absolute h-2 w-2 rotate-45 bg-white', arrowOffset, arrowClassName, {
              'top-[-4px]': arrowType === PopoverArrowTypes.TOP,
              'bottom-[-4px]': arrowType === PopoverArrowTypes.BOTTOM,
              'left-[-4px]': arrowType === PopoverArrowTypes.LEFT,
              'right-[-4px]': arrowType === PopoverArrowTypes.RIGHT
            })}></div>
        )}
        {children}
      </div>,
      portalContainer
    );
  };

  return (
    <>
      {renderAnchor()}
      {renderPortalContent()}
    </>
  );
};

export default PortalPopover;
